import { AgGridReact } from '@ag-grid-community/react';

import { BODS_COLUMN_MAP, QUOTE_META_DATA_COLS_MAP, SUBMITTAL_META_DATA_COLS_MAP, PRICE_RELATED_COLS } from '../constants';
import { PricingDataType } from '../types';
import { useGetBomDataQuery, useAddMultipeBodsMutation } from '../../queries';
import { BodResponse } from '../../queries/apiTypes';
import { useCallback, useMemo } from 'react';
import { useKitPricingUpdate } from './useKitPricingUpdate';
import { isValidCharactersForMfg } from 'src/features/Settings/Manufacturer/utils';
import { getFormattedLeadTimeRange, getValidatedLeadTimeRange } from '../utils';

export const useCreateNewBods = (bomId: string, onPricingInfoEdit: (info: PricingDataType) => Promise<void>, tableRef: React.RefObject<AgGridReact<any>>) => {
    const { handleKitPricingUpdate } = useKitPricingUpdate(bomId);
    const { data: bodsData } = useGetBomDataQuery(bomId!, {
        enabled: Boolean(bomId)
    });
    const { mutateAsync: addMultipleRows, isLoading } = useAddMultipeBodsMutation();

    const bodsMap = useMemo(() => {
        const bodsMap = new Map<number, BodResponse>();
        if (bodsData?.data) {
            bodsData.data.forEach((bod) => {
                bodsMap.set(bod.id, bod);
            });
        }

        return bodsMap;
    }, [bodsData?.data]);

    const prepareBodsPayload = useCallback((newRowsData: string[][], selectedColArr: any[], colsMap: { [index: string]: string }, lastUpdatedBod?: BodResponse, isKitHidden?: boolean) => {
        return newRowsData.flatMap((valueArr) => {
            return valueArr.reduce((acc: any, value: string, index: number) => {
                if (colsMap[selectedColArr?.[index]?.['field']]) {
                    if (colsMap[selectedColArr[index]['field']] === 'qty' && typeof value === 'string') {
                        acc[colsMap[selectedColArr[index]['field']]] = value ? Number(value) : null;
                    } else if (colsMap[selectedColArr[index]['field']] === 'lead_time_value' && typeof value === 'string') {
                        const response = getValidatedLeadTimeRange(value); // pass the string
                        if (!response.isError) {
                            const formattedRange = getFormattedLeadTimeRange({ leadTimeMin: response.tuple[0], leadTimeMax: response.tuple[1] });
                            const withWeeks = formattedRange ? `${formattedRange} weeks` : '';
                            acc[colsMap[selectedColArr[index]['field']]] = withWeeks;
                        } else {
                            acc[colsMap[selectedColArr[index]['field']]] = ''; // Set to null if validation fails
                        }
                    } else if (colsMap[selectedColArr[index]['field']] === 'manufacturer' && typeof value === 'string') {
                        const { isValid, sanitizedValue } = isValidCharactersForMfg(value.trim());
                        acc[colsMap[selectedColArr[index]['field']]] = isValid ? value : sanitizedValue ? sanitizedValue : null;
                    } else if (selectedColArr[index]['field'] in PRICE_RELATED_COLS) {
                        let priceValue = Number(value.replace(/[,$%a-zA-Z]/g, ''));
                        acc[colsMap[selectedColArr[index]['field']]] = priceValue === 0 ? null : priceValue * 100;
                    } else if (selectedColArr[index]['cellDataType'] === 'number') {
                        acc[colsMap[selectedColArr[index]['field']]] = Number(value.replace(/[,$%a-zA-Z]/g, ''));
                    } else {
                        acc[colsMap[selectedColArr[index]['field']]] = value;
                    }
                }

                if (lastUpdatedBod) {
                    ['section', 'substitute_section', 'kit', 'is_hidden'].forEach((item) => {
                        if (lastUpdatedBod.hasOwnProperty(item)) {
                            acc[item] = (lastUpdatedBod as any)[item];
                        }
                    });
                }

                if (isKitHidden != null) {
                    acc['is_hidden'] = isKitHidden;
                }
                return acc;
            }, {});
        });
    }, []);

    const createNewRowsFromClipboard = useCallback(
        async (
            lastUpdatedBodId: number,
            selectedColumns: Set<{
                field?: string;
                cellDataType: string | null;
            }>,
            newRowsData?: string[][]
        ) => {
            if (newRowsData?.length && bodsMap.size) {
                const lastUpdatedBod = bodsMap.get(lastUpdatedBodId);
                let lastUpdatedBodsKit: BodResponse | undefined;
                if (lastUpdatedBod?.kit) {
                    lastUpdatedBodsKit = bodsMap.get(lastUpdatedBod.kit);
                }
                const selectedColsArr = [...selectedColumns];

                const bodsPayload = prepareBodsPayload(newRowsData, selectedColsArr, BODS_COLUMN_MAP, lastUpdatedBod, lastUpdatedBodsKit?.is_hidden);
                const quotesMetaPayload = prepareBodsPayload(newRowsData, selectedColsArr, QUOTE_META_DATA_COLS_MAP);
                const submittalsMetaPayload = prepareBodsPayload(newRowsData, selectedColsArr, SUBMITTAL_META_DATA_COLS_MAP);

                try {
                    const response = await addMultipleRows({
                        bomId,
                        newRowsPayload: {
                            bod_id_list: bodsPayload,
                            quote_metadata: quotesMetaPayload,
                            submittal_metadata: submittalsMetaPayload,
                            last_updated_bod_id: lastUpdatedBodId
                        }
                    });

                    if (
                        response?.status === 201 &&
                        lastUpdatedBodsKit &&
                        !lastUpdatedBodsKit?.is_hidden &&
                        (quotesMetaPayload[0]?.hasOwnProperty('cost_cents') ||
                            quotesMetaPayload[0]?.hasOwnProperty('discounted_cost_cents') ||
                            quotesMetaPayload[0]?.hasOwnProperty('sell_price_cents'))
                    ) {
                        setTimeout(() => {
                            if (lastUpdatedBodsKit?.id) {
                                handleKitPricingUpdate({
                                    onPricingInfoEdit,
                                    tableRef,
                                    type: `copy-paste`
                                });
                            }
                        }, 1500);
                    }
                } catch (e) {
                    console.error(`Error while creating new BOD(s) on Copy/Paste `, e);
                }
            }
        },
        [addMultipleRows, bodsMap, bomId, handleKitPricingUpdate, onPricingInfoEdit, prepareBodsPayload, tableRef]
    );

    return {
        createNewRowsFromClipboard,
        isLoading
    };
};
